/* eslint-disable @typescript-eslint/no-unused-vars */
import { useField, useFormikContext } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { IoIosImages } from 'react-icons/io';
import { MdCancel } from 'react-icons/md';
import { toast } from 'react-toastify';
import { convertFileToBlob } from '../../../utils/fileUtils';
import { compressImage } from '../../../utils/imageUtils';

interface IAcademiaFileInputField {
  btnText: string;
  placeholder: string;
  label: string;
  name: string;
  accept: string;
  isRequired?: boolean;
  resetField?: boolean;
  filePath?: string | null;
}

const AcademiaFileInputField = ({
  accept,
  btnText,
  name,
  label,
  isRequired,
  resetField,
  placeholder,
  filePath,
}: IAcademiaFileInputField) => {
  // integration of formik hooks here
  const [_, meta] = useField({ name });
  const { setFieldValue } = useFormikContext();

  // integration of react hooks here
  const [fileName, setFileName] = useState<string>(
    filePath ? filePath.split('/')[filePath.split('/').length - 1] : ''
  );
  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // resetting filename here
  useEffect(() => {
    if (resetField) {
      setFileName('');
      setIsFileUploaded(false);

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [resetField]);

  // rendering custom file input field component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-legal-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <div className='flex items-center gap-4'>
        <input
          ref={inputRef}
          id={`${name}_field`}
          type='file'
          className='hidden'
          accept={accept}
          onChange={(e) => {
            if (e?.currentTarget?.files) {
              const uploadedFile = e.currentTarget.files[0];

              if (accept.includes('image')) {
                compressImage(uploadedFile, canvasRef)
                  .then((blob) => {
                    setFileName(uploadedFile.name);

                    console.log(
                      'BLOB',
                      blob,
                      uploadedFile.name,
                      uploadedFile.type
                    );

                    setFieldValue(
                      name,
                      new File([blob], uploadedFile.name, {
                        type: uploadedFile.type,
                      })
                    );

                    setIsFileUploaded(true);
                  })
                  .catch((error: any) => {
                    toast.error(error.message);
                  });
              } else {
                convertFileToBlob(uploadedFile)
                  .then((blob) => {
                    setFileName(uploadedFile.name);

                    console.log(
                      'BLOB',
                      blob,
                      uploadedFile.name,
                      uploadedFile.type
                    );

                    setFieldValue(
                      name,
                      new File([blob], uploadedFile.name, {
                        type: uploadedFile.type,
                      })
                    );

                    setIsFileUploaded(true);
                  })
                  .catch((error: any) => {
                    toast.error(error.message);
                  });
              }
            }
          }}
        />

        <div className='relative flex-1'>
          <motion.input
            tabIndex={-1}
            initial={{ x: 0 }}
            animate={
              meta.touched && meta.error
                ? {
                    x: [0, -100, 0, 100, 0],
                    transition: {
                      duration: 0.1,
                      repeat: 2,
                    },
                  }
                : { x: 0 }
            }
            type='text'
            className={`border-2 focus:ring-0 rounded-md p-3 w-full text-legal-dark-blue font-semibold duration-300 text-base ${
              meta.touched && meta.error
                ? 'border-base-red'
                : 'border-legal-dark-blue'
            }`}
            value={fileName}
            placeholder={placeholder}
            readOnly
          />

          {isFileUploaded && (
            <button
              title='Remove uploaded file'
              type='button'
              onClick={() => {
                if (filePath) {
                  setFileName(filePath.split('/')[3]);
                } else {
                  setFileName('');
                }

                setFieldValue(name, '');
                setIsFileUploaded(false);

                if (inputRef.current) {
                  inputRef.current.value = '';
                }
              }}
              className='absolute top-1/2 -translate-y-1/2 right-3 rounded-full hover:opacity-70 active:scale-95 duration-300'
            >
              <MdCancel className='size-7 text-base-red' />
            </button>
          )}
        </div>

        <label
          htmlFor={`${name}_field`}
          className='bg-legal-dark-blue text-base-white rounded-md px-5 py-3 hover:opacity-70 active:scale-95 duration-300 cursor-pointer'
        >
          {btnText}
        </label>
      </div>

      <motion.label
        initial={{ y: -20, opacity: 0, display: 'none' }}
        animate={
          meta.touched && meta.error
            ? { y: 0, opacity: 1, display: 'block' }
            : { y: -20, opacity: 0, transitionEnd: { display: 'none' } }
        }
        htmlFor={`input-${name}`}
        className='block mt-1 text-base-red text-base'
      >
        {meta.error}
      </motion.label>

      {accept.includes('image') ? (
        <>
          <div
            className={`flex items-center justify-center ${
              isFileUploaded && 'mt-2'
            }`}
          >
            <canvas
              ref={canvasRef}
              className={`w-[80px] object-cover rounded-full ${
                !isFileUploaded ? 'h-0' : 'h-[80px]'
              }`}
            ></canvas>
          </div>

          {!isFileUploaded ? (
            filePath ? (
              <div className='mx-auto size-[80px] rounded-full mt-2 overflow-hidden'>
                <img
                  className='size-full object-cover rounded-full'
                  src={filePath}
                  height={0}
                  width={0}
                  sizes='100%'
                  alt='Uploaded file'
                />
              </div>
            ) : (
              <div className='flex items-center justify-center mx-auto h-[80px] w-[80px] rounded-full mt-2 bg-base-light-gray'>
                <IoIosImages className='size-12 text-gray-300' />
              </div>
            )
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default AcademiaFileInputField;
